import React from "react";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, FileSearch, FileWarning, Lock, Clock, Sparkles, Check, UserCheck } from "lucide-react";

const heroImg = "https://images.unsplash.com/photo-1589330694653-ded6df03f754?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRvY3VtZW50JTIwdmVyaWZ5fGVufDB8fHx8MTc4NTAxMTc1M3ww&ixlib=rb-4.1.0&q=85";
const trustImg = "https://images.pexels.com/photos/34092/pexels-photo.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SiteHeader />

      {/* HERO */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 py-16 lg:py-24">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-1 rounded-sm bg-white">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-700">
                UKVI · SPONSOR MANAGEMENT SYSTEM · METADATA FORENSICS
              </span>
            </div>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter font-bold">
              Verify a UKVI<br/>
              Certificate of Sponsorship<br/>
              <span className="text-zinc-400">in under 30 seconds.</span>
            </h1>
            <p className="mt-6 max-w-xl text-zinc-600 text-base leading-relaxed">
              Upload a CoS PDF and our forensic engine inspects producer signatures, XMP metadata,
              modification history and Home Office text markers — cross-referenced with an AI expert reviewer —
              to tell you if the certificate is <span className="font-medium text-zinc-900">genuine</span>,
              <span className="font-medium text-zinc-900"> suspicious</span>, or
              <span className="font-medium text-zinc-900"> likely fake</span>.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register">
                <Button size="lg" className="bg-[#0F172A] hover:bg-[#0F172A]/90 rounded-sm h-11 px-6" data-testid="hero-cta-register">
                  Check a certificate <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="lg" className="rounded-sm h-11 px-6" data-testid="hero-cta-login">
                  Sign in
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              <div className="border-t border-zinc-300 pt-3">
                <div className="font-mono text-2xl font-bold tracking-tight">100+</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Metadata checks</div>
              </div>
              <div className="border-t border-zinc-300 pt-3">
                <div className="font-mono text-2xl font-bold tracking-tight">~15s</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Median result</div>
              </div>
              <div className="border-t border-zinc-300 pt-3">
                <div className="font-mono text-2xl font-bold tracking-tight">0</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Files stored</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative border border-zinc-300 bg-white rounded-sm overflow-hidden">
              <img src={heroImg} alt="Legal certificate close-up" className="w-full h-[420px] object-cover" />
              <div className="absolute top-4 left-4 bg-white/95 border border-zinc-200 rounded-sm px-3 py-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Sample verdict</div>
                <div className="font-heading text-2xl font-bold text-emerald-700 leading-none">GENUINE</div>
                <div className="font-mono text-xs text-zinc-500 mt-1">score 92 / 100</div>
              </div>
              <div className="absolute bottom-4 right-4 bg-[#0F172A] text-white rounded-sm px-3 py-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Producer</div>
                <div className="font-mono text-xs">UKVI SMS / gov.uk</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">01 · Method</div>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-3">
                Deep metadata forensics — cross-checked by an AI expert.
              </h2>
              <p className="text-zinc-600 mt-4 leading-relaxed">
                We combine 20+ deterministic heuristics against known UKVI Sponsor Management System
                fingerprints with a Claude Sonnet 4.5 expert reviewer for reasoning-based verdicts.
              </p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-0 border border-zinc-300 rounded-sm bg-white">
              {[
                {
                  icon: FileSearch,
                  title: "Metadata extraction",
                  body: "Producer, creator, author, XMP, creation & modification timestamps, digital signature, encryption state."
                },
                {
                  icon: FileWarning,
                  title: "Red flag rules",
                  body: "Word / Photoshop / Canva as producer, missing UKVI text markers, edited-after-creation, scrubbed metadata."
                },
                {
                  icon: Sparkles,
                  title: "AI expert review",
                  body: "Claude Sonnet 4.5 evaluates the evidence and produces a verdict with reasoning and confidence."
                },
                {
                  icon: ShieldCheck,
                  title: "Composite score",
                  body: "A single 0–100 genuinity score with a clear GENUINE / SUSPICIOUS / LIKELY_FAKE label."
                },
              ].map((s, i) => (
                <div key={i} className="p-8 border-r border-b border-zinc-200 last:border-b-0 [&:nth-child(2n)]:border-r-0">
                  <s.icon className="w-5 h-5 text-[#0F172A]" />
                  <h3 className="font-heading text-xl font-semibold mt-4">{s.title}</h3>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-12 gap-6 items-end mb-10">
            <div className="lg:col-span-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Pricing</div>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-3">
                Free automated check.<br/>
                <span className="text-zinc-400">£100 for a human verdict.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-zinc-600 text-sm leading-relaxed">
                Start with our free forensic engine. If you need a second pair of expert eyes
                or a certificate of authenticity from our compliance team, book a manual review.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Free tier */}
            <div className="border border-zinc-300 rounded-sm p-8 flex flex-col" data-testid="pricing-free">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Automated</div>
                  <h3 className="font-heading text-2xl font-bold tracking-tight mt-1">Instant Check</h3>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm bg-emerald-100 text-emerald-900 border border-emerald-500">Free</span>
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-heading text-5xl font-bold tracking-tighter">£0</span>
                <span className="font-mono text-xs text-zinc-500">per check</span>
              </div>
              <ul className="mt-8 space-y-3 text-sm text-zinc-700 flex-1">
                {[
                  "PDF metadata & fingerprint forensics",
                  "AI expert review (Claude Sonnet 4.5)",
                  "Genuinity score, red flags, verdict",
                  "Downloadable PDF report",
                  "Full history of your checks",
                ].map((t, i) => (
                  <li key={i} className="flex gap-3">
                    <Check className="w-4 h-4 mt-0.5 text-emerald-700 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register" className="mt-8">
                <Button variant="outline" size="lg" className="w-full rounded-sm h-11" data-testid="pricing-free-cta">
                  Start free <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Expert tier */}
            <div className="border border-zinc-300 rounded-sm p-8 flex flex-col" data-testid="pricing-expert">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Human · In the loop</div>
                  <h3 className="font-heading text-2xl font-bold tracking-tight mt-1">Expert Review</h3>
                </div>
                <UserCheck className="w-6 h-6 text-[#0F172A]" />
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-heading text-5xl font-bold tracking-tighter">£100</span>
                <span className="font-mono text-xs text-zinc-500">GBP · one review</span>
              </div>
              <ul className="mt-8 space-y-3 text-sm text-zinc-700 flex-1">
                {[
                  "Everything in Instant Check",
                  "Manual re-inspection by UKVI compliance team",
                  "Personal written verdict with reasoning",
                  "1:1 communication over email or phone",
                  "Reply within 2 UK business days",
                ].map((t, i) => (
                  <li key={i} className="flex gap-3">
                    <Check className="w-4 h-4 mt-0.5 text-emerald-700 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register" className="mt-8">
                <Button size="lg" variant="outline" className="w-full rounded-sm h-11" data-testid="pricing-expert-cta">
                  Book expert review <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Bundle */}
            <div className="border border-[#0F172A] bg-[#0F172A] text-white rounded-sm p-8 flex flex-col relative" data-testid="pricing-bundle">
              <span className="absolute -top-3 left-6 font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm bg-white text-[#0F172A] border border-[#0F172A]">
                BEST VALUE · SAVE £50
              </span>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">3-pack bundle</div>
                  <h3 className="font-heading text-2xl font-bold tracking-tight mt-1">Expert · 3-Pack</h3>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm bg-emerald-400/20 text-emerald-200 border border-emerald-400/50">
                  3 credits
                </div>
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-heading text-5xl font-bold tracking-tighter">£250</span>
                <span className="font-mono text-xs text-white/60">GBP · one-time</span>
              </div>
              <ul className="mt-8 space-y-3 text-sm text-white/85 flex-1">
                {[
                  "3 expert human reviews (£83 each)",
                  "Credits never expire",
                  "Redeem any time from your dashboard",
                  "Same 2-day SLA on every credit",
                  "Ideal for solicitors & HR teams",
                ].map((t, i) => (
                  <li key={i} className="flex gap-3">
                    <Check className="w-4 h-4 mt-0.5 text-emerald-400 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register" className="mt-8">
                <Button size="lg" className="w-full rounded-sm h-11 bg-white text-[#0F172A] hover:bg-zinc-100" data-testid="pricing-bundle-cta">
                  Get the 3-Pack <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 text-center">
                Secured by Stripe · Razorpay
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAND */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <img src={trustImg} alt="Immigration solicitors" className="w-full h-[360px] object-cover border border-zinc-300 rounded-sm" />
          </div>
          <div className="lg:col-span-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">02 · Built for</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-3">
              Immigration solicitors. HR compliance teams. Sponsored workers & students.
            </h2>
            <ul className="mt-6 space-y-4">
              {[
                { icon: Lock, t: "Private & encrypted", d: "Files are analysed in-memory. We store the metadata report, not your document." },
                { icon: Clock, t: "History audit trail", d: "Every check is logged to your account with a downloadable PDF report." },
                { icon: ShieldCheck, t: "UKVI-specific heuristics", d: "Rules calibrated to the Sponsor Management System, not generic PDF checks." },
              ].map((f, i) => (
                <li key={i} className="flex gap-4 border-l-2 border-zinc-900 pl-4">
                  <f.icon className="w-4 h-4 mt-1 text-[#0F172A] flex-shrink-0" />
                  <div>
                    <div className="font-heading font-semibold">{f.t}</div>
                    <div className="text-sm text-zinc-600">{f.d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">03 · Try it</div>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight mt-3 leading-none">
              Free to try. <span className="text-zinc-500">Instant verdict.</span>
            </h2>
            <p className="text-zinc-400 mt-4 max-w-xl">
              Create a free account, drop in your CoS PDF, and get a full forensic report — no card, no clutter.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/register">
              <Button size="lg" className="bg-white text-[#0F172A] hover:bg-zinc-100 rounded-sm h-12 px-8 font-medium" data-testid="cta-register-bottom">
                Start a check <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="font-mono text-xs text-zinc-500">
            © {new Date().getFullYear()} CoS Verify · Not affiliated with UKVI or the Home Office.
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            Metadata forensics · London
          </div>
        </div>
      </footer>
    </div>
  );
}
